import * as React from 'react'

import { Button, Tooltip, useToggle } from '@factorialco/deprecated-design-system'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { usePortal } from 'modules/core/lib/portalContext'
import app from 'modules/core/store/models/app'

import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import useToggleVisibilityDocument from 'modules/documents/hooks/documents/useToggleVisibilityDocument'

interface Props {
  document: Document
  disabled?: boolean
}

const VisibilityIcon = ({ document, disabled = false }: Props) => {
  const { closePortal } = usePortal()
  const [confirmModalOpen, setConfirmModalOpen] = React.useState(false)
  const [openTooltip, toggleTooltip] = useToggle(false)
  const { loading, toggleVisibilityDocument } = useToggleVisibilityDocument()
  const session = useCurrentSession()
  const currentOwned = document.employee
    ? session.employee?.id === document.employee.id && session.access.id === document.author?.id
    : false
  const isVisible = document.public
  const lockIcon = 'eye-invisible'
  const unlockIcon = 'eye-visible'
  const visibilityIcon = currentOwned || isVisible ? unlockIcon : lockIcon
  const employeeFullName = document.employee?.fullName
  const textModal = employeeFullName
    ? i18n.t('documents.notification_modal.private_description', {
        employee: employeeFullName,
      })
    : i18n.t('documents.notification_modal.public_description')

  const canEdit = document._policies?.edit.can ?? false

  if (!canEdit) return null

  const lockCopy = () => {
    if (currentOwned) {
      return i18n.t('documents.own_privacy')
    }
    if (isVisible) {
      return employeeFullName
        ? i18n.t('documents.public', { name: employeeFullName })
        : i18n.t('documents.company_public')
    }
    return employeeFullName
      ? i18n.t('documents.private', { name: employeeFullName })
      : i18n.t('documents.company_private')
  }

  const onSubmit = async (sendNotifications: boolean) => {
    const result = await toggleVisibilityDocument({
      id: document.id,
      sendNotifications,
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }

    closePortal()
    setConfirmModalOpen(false)
  }

  const onClick = () => {
    if (currentOwned) return

    if (isVisible) {
      onSubmit(false)
    } else {
      setConfirmModalOpen(true)
    }
  }

  return (
    <>
      <Tooltip.Alt
        attachment="bottom center"
        open={openTooltip}
        label={lockCopy()}
        aria-label={lockCopy()}
        toggle={toggleTooltip}
      >
        <Button
          onClick={onClick}
          type="secondary"
          disabled={disabled || currentOwned || loading}
          iconName={visibilityIcon}
          aria-label={visibilityIcon}
        ></Button>
      </Tooltip.Alt>
      {confirmModalOpen && (
        <Modal
          illustration="visibility"
          title={i18n.t('documents.notification_modal.title')}
          description={textModal}
          onClose={() => {
            setConfirmModalOpen(false)
          }}
          footer={
            <>
              <Button type="secondary" disabled={loading} onClick={() => onSubmit(false)}>
                {i18n.t('documents.notification_modal.buttons.do_not_send')}
              </Button>
              <Button disabled={loading} onClick={() => onSubmit(true)}>
                {i18n.t('documents.notification_modal.buttons.send_notification')}
              </Button>
            </>
          }
        />
      )}
    </>
  )
}

export default VisibilityIcon
